import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/use-toast';

export interface SupabaseChartOfAccountLevel1 {
  id: string;
  code: string;
  name: string;
  color?: string;
}

export interface SupabaseChartOfAccountLevel2 {
  id: string;
  level1_id: string;
  code: string;
  name: string;
  type: 'receita' | 'despesa';
  color?: string;
}

export interface SupabaseChartOfAccount {
  id: string;
  name: string;
  color?: string;
  type: 'receita' | 'despesa';
  dre_category?: string;
  hidden?: boolean;
  level2_id?: string;
  code?: string;
}

export interface SupabaseAccount {
  id: string;
  name: string;
  color?: string;
  initial_balance?: number;
  agencia?: string | null;
  conta_corrente?: string | null;
}

export interface SupabaseCostCenter {
  id: string;
  name: string;
}

export interface SupabasePaymentMethod {
  id: string;
  name: string;
}

export interface SupabaseSupplier {
  id: string;
  name: string;
  cnpj?: string | null;
  anexo?: string | null;
}

export interface SupabaseTransaction {
  id: string;
  description: string;
  value: number;
  type: 'receita' | 'despesa';
  chart_of_accounts_id?: string;
  account_id?: string;
  cost_center_id?: string;
  payment_method_id?: string;
  supplier_id?: string;
  issue_date?: string;
  due_date?: string;
  payment_date?: string;
  settled?: boolean;
  parcel_mode?: 'total' | 'parcela';
  parcels?: number;
  parcel_index?: number;
  issue_date_mode?: 'fixed' | 'followDue';
  kind?: 'transferencia' | 'normal';
  transfer_group_id?: string;
  sale_id?: string;
  purchase_id?: string;
  reconciled?: boolean;
  reconciled_at?: string | null;
  ofx_fitid?: string | null;
  ofx_import_id?: string | null;
  updated_at?: string;
}

export interface SupabaseProduct {
  id: string;
  user_id: string;
  name: string;
  category_id?: string;
  supplier_id?: string;
  unit_price?: number;
  stock?: number;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export const useSupabaseSync = () => {
  const { user } = useAuth();

  // ============ Chart of Accounts Level 1 ============
  const fetchChartOfAccountsLevel1 = useCallback(async (): Promise<SupabaseChartOfAccountLevel1[]> => {
    if (!user) return [];
    
    const { data, error } = await supabase
      .from('chart_of_accounts_level1')
      .select('*')
      .order('code');

    if (error) {
      console.error('Error fetching chart of accounts level1:', error);
      return [];
    }

    return data || [];
  }, [user]);

  const createChartOfAccountLevel1 = useCallback(async (account: Omit<SupabaseChartOfAccountLevel1, 'id'>): Promise<string | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('chart_of_accounts_level1')
      .insert([{
        user_id: user.id,
        code: account.code,
        name: account.name,
        color: account.color || '#64748B',
      }])
      .select('id')
      .single();

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Falha ao criar nível 1 do plano de contas.",
      });
      return null;
    }

    return data.id;
  }, [user]);

  const updateChartOfAccountLevel1 = useCallback(async (id: string, updates: Partial<SupabaseChartOfAccountLevel1>): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('chart_of_accounts_level1')
      .update({
        ...(updates.code && { code: updates.code }),
        ...(updates.name && { name: updates.name }),
        ...(updates.color && { color: updates.color }),
      })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o nível 1.",
      });
      return false;
    }

    return true;
  }, [user]);

  const deleteChartOfAccountLevel1 = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;

    const { data, error } = await supabase
      .from('chart_of_accounts_level1')
      .delete()
      .eq('id', id)
      .select('id');

    if (error || !data?.length) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error?.message || "O nível 1 não foi excluído. Verifique sua permissão.",
      });
      return false;
    }

    return true;
  }, [user]);

  // ============ Chart of Accounts Level 2 ============
  const fetchChartOfAccountsLevel2 = useCallback(async (): Promise<SupabaseChartOfAccountLevel2[]> => {
    if (!user) return [];
    
    const { data, error } = await supabase
      .from('chart_of_accounts_level2')
      .select('*')
      .order('code');

    if (error) {
      console.error('Error fetching chart of accounts level2:', error);
      return [];
    }

    return (data || []).map(item => ({
      ...item,
      type: item.type as 'receita' | 'despesa'
    }));
  }, [user]);

  const createChartOfAccountLevel2 = useCallback(async (account: Omit<SupabaseChartOfAccountLevel2, 'id'>): Promise<string | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('chart_of_accounts_level2')
      .insert([{
        user_id: user.id,
        level1_id: account.level1_id,
        code: account.code,
        name: account.name,
        type: account.type,
        color: account.color || '#64748B',
      }])
      .select('id')
      .single();

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Falha ao criar nível 2 do plano de contas.",
      });
      return null;
    }

    return data.id;
  }, [user]);

  const updateChartOfAccountLevel2 = useCallback(async (id: string, updates: Partial<SupabaseChartOfAccountLevel2>): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('chart_of_accounts_level2')
      .update({
        ...(updates.level1_id && { level1_id: updates.level1_id }),
        ...(updates.code && { code: updates.code }),
        ...(updates.name && { name: updates.name }),
        ...(updates.type && { type: updates.type }),
        ...(updates.color && { color: updates.color }),
      })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o nível 2.",
      });
      return false;
    }

    return true;
  }, [user]);

  const deleteChartOfAccountLevel2 = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;

    const { data, error } = await supabase
      .from('chart_of_accounts_level2')
      .delete()
      .eq('id', id)
      .select('id');

    if (error || !data?.length) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error?.message || "O nível 2 não foi excluído. Verifique sua permissão.",
      });
      return false;
    }

    return true;
  }, [user]);

  // ============ Chart of Accounts (Nível 3) ============
  const fetchChartOfAccounts = useCallback(async (): Promise<SupabaseChartOfAccount[]> => {
    if (!user) return [];
    
    const { data, error } = await supabase
      .from('chart_of_accounts')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching chart of accounts:', error);
      return [];
    }

    return (data || []).map(item => ({
      ...item,
      type: item.type as 'receita' | 'despesa'
    }));
  }, [user]);

  const createChartOfAccount = useCallback(async (account: Omit<SupabaseChartOfAccount, 'id'>): Promise<string | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('chart_of_accounts')
      .insert([{
        user_id: user.id,
        name: account.name,
        color: account.color || '#64748B',
        type: account.type,
        dre_category: account.dre_category,
        hidden: account.hidden || false,
        level2_id: account.level2_id || null,
        code: account.code || null,
      }])
      .select('id')
      .single();

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Falha ao criar conta do plano de contas.",
      });
      return null;
    }

    return data.id;
  }, [user]);

  const updateChartOfAccount = useCallback(async (id: string, updates: Partial<SupabaseChartOfAccount>): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('chart_of_accounts')
      .update({
        ...(updates.name && { name: updates.name }),
        ...(updates.color && { color: updates.color }),
        ...(updates.type && { type: updates.type }),
        ...(updates.dre_category !== undefined && { dre_category: updates.dre_category }),
        ...(updates.hidden !== undefined && { hidden: updates.hidden }),
        ...(updates.level2_id !== undefined && { level2_id: updates.level2_id }),
        ...(updates.code !== undefined && { code: updates.code }),
      })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar a conta do plano de contas.",
      });
      return false;
    }

    return true;
  }, [user]);

  const deleteChartOfAccount = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;

    const { data, error } = await supabase
      .from('chart_of_accounts')
      .delete()
      .eq('id', id)
      .select('id');

    if (error || !data?.length) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error?.message || "A conta não foi excluída. Verifique sua permissão.",
      });
      return false;
    }

    return true;
  }, [user]);

  // ============ Accounts ============
  const fetchAccounts = useCallback(async (): Promise<SupabaseAccount[]> => {
    if (!user) return [];
    
    const { data, error } = await supabase
      .from('accounts')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching accounts:', error);
      return [];
    }

    return data || [];
  }, [user]);

  const createAccount = useCallback(async (account: Omit<SupabaseAccount, 'id'>): Promise<string | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('accounts')
      .insert([{
        user_id: user.id,
        name: account.name,
        color: account.color || '#64748B',
        initial_balance: account.initial_balance || 0,
        agencia: account.agencia || null,
        conta_corrente: account.conta_corrente || null,
      }])
      .select('id')
      .single();

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível criar a conta.",
      });
      return null;
    }

    return data.id;
  }, [user]);

  const updateAccount = useCallback(async (id: string, updates: Partial<SupabaseAccount>): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('accounts')
      .update({
        ...(updates.name && { name: updates.name }),
        ...(updates.color && { color: updates.color }),
        ...(updates.initial_balance !== undefined && { initial_balance: updates.initial_balance }),
        ...(updates.agencia !== undefined && { agencia: updates.agencia || null }),
        ...(updates.conta_corrente !== undefined && { conta_corrente: updates.conta_corrente || null }),
      })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar a conta.",
      });
      return false;
    }

    return true;
  }, [user]);

  const deleteAccount = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('accounts')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível excluir a conta.",
      });
      return false;
    }

    return true;
  }, [user]);

  // ============ Cost Centers ============
  const fetchCostCenters = useCallback(async (): Promise<SupabaseCostCenter[]> => {
    if (!user) return [];
    
    const { data, error } = await supabase
      .from('cost_centers')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching cost centers:', error);
      return [];
    }

    return data || [];
  }, [user]);

  const createCostCenter = useCallback(async (costCenter: Omit<SupabaseCostCenter, 'id'>): Promise<string | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('cost_centers')
      .insert([{
        user_id: user.id,
        name: costCenter.name,
      }])
      .select('id')
      .single();

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível criar o centro de custos.",
      });
      return null;
    }

    return data.id;
  }, [user]);

  const updateCostCenter = useCallback(async (id: string, updates: Partial<SupabaseCostCenter>): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('cost_centers')
      .update({
        ...(updates.name && { name: updates.name }),
      })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o centro de custos.",
      });
      return false;
    }

    return true;
  }, [user]);

  const deleteCostCenter = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('cost_centers')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível excluir o centro de custos.",
      });
      return false;
    }

    return true;
  }, [user]);

  // ============ Payment Methods ============
  const fetchPaymentMethods = useCallback(async (): Promise<SupabasePaymentMethod[]> => {
    if (!user) return [];
    
    const { data, error } = await supabase
      .from('payment_methods')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching payment methods:', error);
      return [];
    }

    return data || [];
  }, [user]);

  const createPaymentMethod = useCallback(async (paymentMethod: Omit<SupabasePaymentMethod, 'id'>): Promise<string | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('payment_methods')
      .insert([{
        user_id: user.id,
        name: paymentMethod.name,
      }])
      .select('id')
      .single();

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível criar a forma de pagamento.",
      });
      return null;
    }

    return data.id;
  }, [user]);

  const updatePaymentMethod = useCallback(async (id: string, updates: Partial<SupabasePaymentMethod>): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('payment_methods')
      .update({
        ...(updates.name && { name: updates.name }),
      })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar a forma de pagamento.",
      });
      return false;
    }

    return true;
  }, [user]);

  const deletePaymentMethod = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('payment_methods')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível excluir a forma de pagamento.",
      });
      return false;
    }

    return true;
  }, [user]);

  // ============ Suppliers ============
  const fetchSuppliers = useCallback(async (): Promise<SupabaseSupplier[]> => {
    if (!user) return [];
    
    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching suppliers:', error);
      return [];
    }

    return data || [];
  }, [user]);

  const createSupplier = useCallback(async (supplier: Omit<SupabaseSupplier, 'id'>): Promise<string | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('suppliers')
      .insert([{
        user_id: user.id,
        name: supplier.name,
        cnpj: supplier.cnpj ?? null,
        anexo: supplier.anexo ?? null,
      }])
      .select('id')
      .single();

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível criar o fornecedor.",
      });
      return null;
    }

    return data.id;
  }, [user]);

  const updateSupplier = useCallback(async (id: string, updates: Partial<SupabaseSupplier>): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('suppliers')
      .update({
        ...(updates.name && { name: updates.name }),
        ...(updates.cnpj !== undefined && { cnpj: updates.cnpj || null }),
        ...(updates.anexo !== undefined && { anexo: updates.anexo || null }),
      })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o fornecedor.",
      });
      return false;
    }

    return true;
  }, [user]);

  const deleteSupplier = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('suppliers')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível excluir o fornecedor.",
      });
      return false;
    }

    return true;
  }, [user]);

  // ============ Transactions ============
  // Paginação automática para buscar TODOS os lançamentos (sem limite de 1000)
  // OTIMIZAÇÃO EGRESS: select explícito (sem user_id/created_at/organization_id)
  // e suporte a sincronização incremental via parâmetro `sinceIso` (updated_at > sinceIso)
  const fetchTransactions = useCallback(async (sinceIso?: string): Promise<SupabaseTransaction[]> => {
    if (!user) return [];

    const TRANSACTION_COLUMNS =
      'id,description,value,type,chart_of_accounts_id,account_id,cost_center_id,payment_method_id,supplier_id,issue_date,due_date,payment_date,settled,parcel_mode,parcels,parcel_index,issue_date_mode,kind,transfer_group_id,sale_id,purchase_id,reconciled,reconciled_at,ofx_fitid,ofx_import_id,updated_at';

    const PAGE_SIZE = 1000;
    let from = 0;
    const allTransactions: SupabaseTransaction[] = [];

    // Mapa para garantir deduplicação por id (paginação pode duplicar quando
    // muitos registros compartilham o mesmo updated_at em importações em massa)
    const seen = new Map<string, SupabaseTransaction>();

    while (true) {
      let query = supabase
        .from('transactions')
        .select(TRANSACTION_COLUMNS)
        // Ordenação estável: id como desempate evita perda/duplicação de linhas
        // entre páginas quando updated_at é idêntico (importações em massa).
        .order('id', { ascending: true })
        .range(from, from + PAGE_SIZE - 1);

      if (sinceIso) {
        query = query.gt('updated_at', sinceIso);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching transactions:', error);
        return [];
      }

      if (!data || data.length === 0) {
        break;
      }

      for (const item of data as any[]) {
        seen.set(item.id, {
          ...item,
          type: item.type as 'receita' | 'despesa',
          parcel_mode: item.parcel_mode as 'total' | 'parcela' | undefined,
          kind: item.kind as 'transferencia' | 'normal' | undefined,
          issue_date_mode: item.issue_date_mode as 'fixed' | 'followDue' | undefined,
        });
      }

      // Se retornou menos que PAGE_SIZE, não há mais páginas
      if (data.length < PAGE_SIZE) {
        break;
      }

      from += PAGE_SIZE;
    }

    allTransactions.push(...seen.values());
    return allTransactions;
  }, [user]);

  const createTransaction = useCallback(async (transaction: Omit<SupabaseTransaction, 'id'>): Promise<string> => {
    if (!user) {
      throw new Error('Usuário não autenticado');
    }

    const { data, error } = await supabase
      .from('transactions')
      .insert([{
        user_id: user.id,
        description: transaction.description,
        value: transaction.value,
        type: transaction.type,
        chart_of_accounts_id: transaction.chart_of_accounts_id,
        account_id: transaction.account_id,
        cost_center_id: transaction.cost_center_id,
        payment_method_id: transaction.payment_method_id,
        supplier_id: transaction.supplier_id,
        issue_date: transaction.issue_date,
        due_date: transaction.due_date,
        payment_date: transaction.payment_date,
        settled: transaction.settled,
        parcel_mode: transaction.parcel_mode,
        parcels: transaction.parcels,
        parcel_index: transaction.parcel_index,
        issue_date_mode: transaction.issue_date_mode,
        kind: transaction.kind,
        transfer_group_id: transaction.transfer_group_id,
        sale_id: transaction.sale_id,
        purchase_id: transaction.purchase_id,
      }])
      .select('id')
      .single();

    if (error) {
      console.error('Erro ao criar transação no Supabase:', error);
      throw new Error(error.message || 'Não foi possível criar a transação.');
    }

    if (!data?.id) {
      throw new Error('Resposta inválida do Supabase: ID não retornado');
    }

    return data.id;
  }, [user]);

  const updateTransaction = useCallback(async (id: string, updates: Partial<SupabaseTransaction>): Promise<boolean> => {
    if (!user) return false;

    // IMPORTANTE: enviar TODO campo explicitamente informado, inclusive null/"".
    // Filtrar por "truthy" fazia com que limpezas (estorno = payment_date null,
    // remover fornecedor/centro de custo etc.) nunca fossem persistidas — a tela
    // mostrava a mudança e a sincronização seguinte trazia o valor antigo de volta.
    const FIELDS: Array<keyof SupabaseTransaction> = [
      'description', 'value', 'type', 'chart_of_accounts_id', 'account_id',
      'cost_center_id', 'payment_method_id', 'supplier_id', 'issue_date',
      'due_date', 'payment_date', 'settled', 'parcel_mode', 'parcels',
      'parcel_index', 'issue_date_mode', 'kind', 'transfer_group_id',
      'sale_id', 'purchase_id', 'reconciled', 'reconciled_at',
      'ofx_fitid', 'ofx_import_id',
    ];

    const payload: Partial<SupabaseTransaction> = {};
    for (const field of FIELDS) {
      if (Object.prototype.hasOwnProperty.call(updates, field) && (updates as any)[field] !== undefined) {
        const v = (updates as any)[field];
        // Colunas opcionais: string vazia vira null para não quebrar FKs/datas
        (payload as Record<keyof SupabaseTransaction, unknown>)[field] = v === '' ? null : v;
      }
    }

    if (Object.keys(payload).length === 0) return true;

    // Sem filtro por user_id: a RLS já garante o acesso e o filtro impedia
    // que membros da organização editassem lançamentos compartilhados
    // (0 linhas afetadas, sem erro → "rollback" silencioso).
    const { data, error } = await supabase
      .from('transactions')
      .update(payload)
      .eq('id', id)
      .select('id');

    if (error) {
      throw new Error(error.message || 'Não foi possível atualizar a transação.');
    }

    if (!data || data.length === 0) {
      throw new Error('A alteração não foi salva: lançamento não encontrado ou sem permissão.');
    }

    return true;
  }, [user]);

  const deleteTransaction = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;

    const { data, error } = await supabase
      .from('transactions')
      .delete()
      .eq('id', id)
      .select('id');

    if (error) {
      throw new Error(error.message || 'Não foi possível excluir a transação.');
    }

    if (!data || data.length === 0) {
      throw new Error('A exclusão não foi concluída: lançamento não encontrado ou sem permissão.');
    }

    return true;
  }, [user]);

  // Bulk insert atômico: se qualquer chunk falhar, faz rollback dos IDs já inseridos
  const bulkCreateTransactions = useCallback(
    async (
      items: Array<Omit<SupabaseTransaction, 'id'>>,
      onProgress?: (done: number, total: number) => void
    ): Promise<{ ids: string[]; inserted: number; failed: number; errors: string[] }> => {
      if (!user) throw new Error('Usuário não autenticado');
      const CHUNK = 200;
      const ids: string[] = [];
      const errors: string[] = [];
      let inserted = 0;
      let aborted = false;

      for (let i = 0; i < items.length; i += CHUNK) {
        const chunk = items.slice(i, i + CHUNK).map((t) => ({
          user_id: user.id,
          description: t.description,
          value: t.value,
          type: t.type,
          chart_of_accounts_id: t.chart_of_accounts_id,
          account_id: t.account_id,
          cost_center_id: t.cost_center_id,
          payment_method_id: t.payment_method_id,
          supplier_id: t.supplier_id,
          issue_date: t.issue_date,
          due_date: t.due_date,
          payment_date: t.payment_date,
          settled: t.settled,
          parcel_mode: t.parcel_mode,
          parcels: t.parcels,
          parcel_index: t.parcel_index,
          issue_date_mode: t.issue_date_mode,
          kind: t.kind ?? 'normal',
          transfer_group_id: t.transfer_group_id,
          sale_id: t.sale_id,
          purchase_id: t.purchase_id,
        }));

        const { data, error } = await supabase
          .from('transactions')
          .insert(chunk)
          .select('id');

        if (error) {
          errors.push(`Chunk ${Math.floor(i / CHUNK) + 1}: ${error.message}`);
          console.error('bulkCreateTransactions chunk error:', error);
          aborted = true;
          break;
        }

        const got = (data || []) as Array<{ id: string }>;
        if (got.length !== chunk.length) {
          errors.push(`Chunk ${Math.floor(i / CHUNK) + 1}: enviadas ${chunk.length} linhas mas o banco confirmou apenas ${got.length}.`);
          for (const r of got) ids.push(r.id);
          inserted += got.length;
          aborted = true;
          break;
        }
        for (const r of got) ids.push(r.id);
        inserted += got.length;

        onProgress?.(Math.min(i + CHUNK, items.length), items.length);
      }

      // Rollback atômico: se abortou, apaga tudo que já foi gravado para não deixar dados parciais
      if (aborted) {
        if (ids.length > 0) {
          const ROLLBACK_CHUNK = 200;
          for (let i = 0; i < ids.length; i += ROLLBACK_CHUNK) {
            const slice = ids.slice(i, i + ROLLBACK_CHUNK);
            await supabase
              .from('transactions')
              .delete()
              .in('id', slice)
              .eq('user_id', user.id);
          }
          errors.unshift(`Rollback aplicado: ${ids.length} lançamento(s) já gravados foram removidos para manter a integridade.`);
        }
        return { ids: [], inserted: 0, failed: items.length, errors };
      }

      return { ids, inserted, failed: 0, errors };
    },
    [user]
  );

  // Bulk delete em chunks
  const bulkDeleteTransactions = useCallback(
    async (
      ids: string[],
      onProgress?: (done: number, total: number) => void
    ): Promise<{ deleted: number; failed: number; errors: string[] }> => {
      if (!user) return { deleted: 0, failed: ids.length, errors: ['Não autenticado'] };
      const CHUNK = 200;
      let deleted = 0;
      let failed = 0;
      const errors: string[] = [];

      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        const { error, count } = await supabase
          .from('transactions')
          .delete({ count: 'exact' })
          .in('id', chunk)
          .eq('user_id', user.id);

        if (error) {
          failed += chunk.length;
          errors.push(error.message);
          console.error('bulkDeleteTransactions chunk error:', error);
        } else {
          deleted += typeof count === 'number' ? count : chunk.length;
        }

        onProgress?.(Math.min(i + CHUNK, ids.length), ids.length);
      }

      return { deleted, failed, errors };
    },
    [user]
  );

  return {
    // Chart of Accounts Level 1
    fetchChartOfAccountsLevel1,
    createChartOfAccountLevel1,
    updateChartOfAccountLevel1,
    deleteChartOfAccountLevel1,
    // Chart of Accounts Level 2
    fetchChartOfAccountsLevel2,
    createChartOfAccountLevel2,
    updateChartOfAccountLevel2,
    deleteChartOfAccountLevel2,
    // Chart of Accounts (Nível 3)
    fetchChartOfAccounts,
    createChartOfAccount,
    updateChartOfAccount,
    deleteChartOfAccount,
    // Accounts
    fetchAccounts,
    createAccount,
    updateAccount,
    deleteAccount,
    // Cost Centers
    fetchCostCenters,
    createCostCenter,
    updateCostCenter,
    deleteCostCenter,
    // Payment Methods
    fetchPaymentMethods,
    createPaymentMethod,
    updatePaymentMethod,
    deletePaymentMethod,
    // Suppliers
    fetchSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    // Transactions
    fetchTransactions,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    bulkCreateTransactions,
    bulkDeleteTransactions,
  };
};
